/**
 * Pollen Dots — Phase 1 CRM Queries list (mobile-first).
 *
 * Lists every query (lead) the team has logged. Each row shows the
 * customer, source, status, task counts, and when it was created.
 * Tap a row to open the workspace; "Log Query" floats at the bottom
 * for thumb-friendly access during a phone call.
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import PollenDotsNav from '@/components/PollenDotsNav';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Plus, Search, Loader2, MessageCircle, Phone, User, ListChecks, Inbox,
} from 'lucide-react';
import { API } from '@/App';

const authHeaders = () => {
  const t = localStorage.getItem('session_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const STATUSES = ['Open', 'Unassigned', 'In Progress', 'Itinerary Shared', 'Quoted', 'Invoiced', 'Tour Created', 'Closed'];
const STATUS_COLORS = {
  'Open':             'bg-amber-100 text-amber-900 border-amber-200',
  'Unassigned':       'bg-orange-100 text-orange-900 border-orange-200',
  'In Progress':      'bg-blue-100 text-blue-900 border-blue-200',
  'Itinerary Shared': 'bg-sky-100 text-sky-900 border-sky-200',
  'Quoted':           'bg-violet-100 text-violet-900 border-violet-200',
  'Invoiced':         'bg-fuchsia-100 text-fuchsia-900 border-fuchsia-200',
  'Tour Created':     'bg-emerald-100 text-emerald-900 border-emerald-200',
  'Closed':           'bg-slate-200 text-slate-700 border-slate-300',
};

const PollenDotsQueries = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set('q', q);
      if (status) params.set('status', status);
      params.set('limit', '200');
      const res = await fetch(`${API}/pollen/queries?${params.toString()}`, {
        credentials: 'include', headers: authHeaders(),
      });
      if (res.ok) setRows(await res.json());
    } finally { setLoading(false); }
  }, [q, status]);

  // Debounce search by 250ms — feels instant on mobile keyboards
  useEffect(() => {
    const id = setTimeout(load, 250);
    return () => clearTimeout(id);
  }, [load]);

  // Re-fetch when the tab regains focus so task-count badges & query
  // statuses reflect edits made elsewhere (e.g. user switched to the
  // workspace, changed a task, then came back).
  useEffect(() => {
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [load]);

  const totals = useMemo(() => {
    const t = {};
    for (const s of STATUSES) t[s] = 0;
    for (const r of rows) {
      const s = r.status || 'Open';
      if (s in t) t[s] += 1;
    }
    return t;
  }, [rows]);

  return (
    <Layout>
      <div className="space-y-4" data-testid="queries-page">
        <PollenDotsNav />

        <div className="flex items-center justify-between gap-2">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold tracking-tight">Queries</h1>
            <p className="text-sm text-muted-foreground">All incoming leads — log fast, follow up faster.</p>
          </div>
          <Button
            onClick={() => navigate('/pollen-dots/queries/new')}
            className="h-11 px-4 bg-amber-600 hover:bg-amber-700 text-white shadow"
            data-testid="btn-log-query-top"
          >
            <Plus className="w-4 h-4 mr-1" />Log Query
          </Button>
        </div>

        {/* Lifecycle pill counts — tap any pill to filter the list to that
            stage. Tap again (or the highlighted one) to clear the filter. */}
        <div className="grid grid-cols-4 gap-2">
          {[
            { k: 'Open',             l: 'Open' },
            { k: 'Unassigned',       l: 'Unassigned' },
            { k: 'In Progress',      l: 'In Prog' },
            { k: 'Itinerary Shared', l: 'Itin Shared' },
            { k: 'Quoted',           l: 'Quoted' },
            { k: 'Invoiced',         l: 'Invoiced' },
            { k: 'Tour Created',     l: 'Tour' },
            { k: 'Closed',           l: 'Closed' },
          ].map(({ k, l }) => {
            const active = status === k;
            return (
              <button
                key={k}
                type="button"
                onClick={() => setStatus(active ? '' : k)}
                className={`rounded-lg border p-2 text-center transition active:scale-[0.97] ${active ? 'bg-amber-50 border-amber-400 ring-2 ring-amber-300' : 'bg-card hover:bg-muted/40'}`}
                data-testid={`pill-${k.toLowerCase().replace(/\s+/g, '_')}`}
              >
                <div className={`text-[10px] uppercase tracking-wide truncate ${active ? 'text-amber-900 font-semibold' : 'text-muted-foreground'}`}>{l}</div>
                <div className="text-base font-semibold" data-testid={`count-${k.toLowerCase().replace(/\s+/g, '_')}`}>{totals[k]}</div>
              </button>
            );
          })}
        </div>

        {/* Search + Filter */}
        <Card>
          <CardContent className="p-3 space-y-2">
            <div className="relative">
              <Search className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
              <Input
                value={q}
                onChange={e => setQ(e.target.value)}
                placeholder="Search by name, mobile, QRY-id, or notes"
                className="h-11 pl-10 text-base"
                data-testid="queries-search"
              />
            </div>
            <Select value={status || '__all'} onValueChange={v => setStatus(v === '__all' ? '' : v)}>
              <SelectTrigger className="h-11" data-testid="queries-status-filter">
                <SelectValue placeholder="All statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">All statuses</SelectItem>
                {STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {/* List */}
        {loading ? (
          <div className="flex items-center justify-center py-10 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading…
          </div>
        ) : rows.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center space-y-3">
              <Inbox className="w-10 h-10 mx-auto text-muted-foreground" />
              <div>
                <div className="font-medium">No queries yet</div>
                <div className="text-xs text-muted-foreground">
                  Log a query the moment a lead comes in over WhatsApp or phone.
                </div>
              </div>
              <Button
                onClick={() => navigate('/pollen-dots/queries/new')}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="btn-log-query-empty"
              >
                <Plus className="w-4 h-4 mr-1" />Log your first query
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {rows.map(r => (
              <button
                key={r.id}
                type="button"
                onClick={() => navigate(`/pollen-dots/queries/${r.id}`)}
                className="w-full text-left rounded-lg border bg-card hover:bg-muted/30 transition p-3 min-h-[72px]"
                data-testid={`query-row-${r.id}`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-full bg-amber-100 text-amber-800 flex items-center justify-center shrink-0">
                    <User className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold truncate">
                        {r.customer_name || '(provisional)'}
                      </span>
                      <span className="text-[11px] text-muted-foreground font-mono">{r.query_id}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5 text-xs text-muted-foreground flex-wrap">
                      {r.customer_phone && (
                        <span className="inline-flex items-center gap-1">
                          <Phone className="w-3 h-3" />{r.customer_phone}
                        </span>
                      )}
                      <span className="inline-flex items-center gap-1">
                        <MessageCircle className="w-3 h-3" />{r.source}
                      </span>
                      {r.task_counts && r.task_counts.total > 0 && (
                        <span className="inline-flex items-center gap-1">
                          <ListChecks className="w-3 h-3" />
                          {r.task_counts.open}/{r.task_counts.total}
                        </span>
                      )}
                    </div>
                    {r.requirement_notes && (
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                        {r.requirement_notes}
                      </p>
                    )}
                  </div>
                  <Badge variant="outline" className={`text-[10px] shrink-0 ${STATUS_COLORS[r.status] || ''}`}>
                    {r.status}
                  </Badge>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default PollenDotsQueries;
